import PropTypes from "prop-types";
import React, { useEffect, useRef } from "react";
import { connect } from "react-redux";
import MenuCart from "./sub-components/MenuCart";
import LinkWrapper from "../linkWrapper/LinkWrapper";
import { removeFromCart } from "../../../redux/actions/cartActions";
import { constructArtificialLinkNode, getStaticPageLink } from "../../../utils/links";

// Static storefront pages (compare, wishlist, account, cart) live under the current shop base
// and may carry a language prefix, never a bare "/compare" (see utils/links.js).
const staticLink = (pageType) => constructArtificialLinkNode({ value: getStaticPageLink(pageType) });

// Account, compare, wishlist and cart icons with optional text labels. Compare and wishlist can
// be switched off in the navigation; the cart opens its dropdown on large screens and links to
// the cart page on small ones (CSS decides which of the two is visible).
const IconGroup = ({ currency, cartData, wishlistData, compareData, removeFromCart, showCompare = true, showWishlist = true, showLabels = true }) => {
    const cartWrapRef = useRef(null);

    // The badge shows the number of items (sum of quantities), not the number of cart lines.
    const cartCount = (cartData || []).reduce((sum, item) => sum + (Number(item.quantity) || 1), 0);

    const toggleDropdown = (e) => {
        e.currentTarget.nextSibling.classList.toggle("active");
    };

    // Close the cart dropdown when clicking anywhere outside it, or when a link inside it
    // (view cart, checkout, a product) is followed.
    useEffect(() => {
        const closeOnOutsideClick = (e) => {
            const wrap = cartWrapRef.current;
            if (!wrap || wrap.contains(e.target)) return;
            const cartContent = wrap.querySelector(".shopping-cart-content");
            if (cartContent) cartContent.classList.remove("active");
        };

        document.addEventListener("mousedown", closeOnOutsideClick);
        return () => document.removeEventListener("mousedown", closeOnOutsideClick);
    }, []);

    const closeCartOnLinkClick = (e) => {
        if (!e.target.closest("a")) return;
        const cartContent = cartWrapRef.current && cartWrapRef.current.querySelector(".shopping-cart-content");
        if (cartContent) cartContent.classList.remove("active");
    };

    const Label = ({ children }) => (showLabels ? <span className="shop-header__icon-label">{children}</span> : null);

    return (
        <div className="header-right-wrap shop-header__icons">
            <div className="same-style account-setting shop-header__icon-item">
                <button type="button" className="account-setting-active shop-header__icon" onClick={toggleDropdown} aria-label="Account">
                    <i className="pe-7s-user-female" />
                    <Label>Account</Label>
                </button>
                <div className="account-dropdown">
                    <ul>
                        <li>
                            <LinkWrapper linkNode={staticLink("LOGIN_REGISTER")}>Login</LinkWrapper>
                        </li>
                        <li>
                            <LinkWrapper linkNode={staticLink("LOGIN_REGISTER")}>Register</LinkWrapper>
                        </li>
                        <li>
                            <LinkWrapper linkNode={staticLink("MY_ACCOUNT")}>my account</LinkWrapper>
                        </li>
                    </ul>
                </div>
            </div>

            {showCompare && (
                <div className="same-style header-compare shop-header__icon-item shop-header__icon-item--compare">
                    <LinkWrapper linkNode={staticLink("COMPARE")} linkClass="shop-header__icon">
                        <i className="pe-7s-shuffle" />
                        <span className="count-style">{compareData && compareData.length ? compareData.length : 0}</span>
                        <Label>Compare</Label>
                    </LinkWrapper>
                </div>
            )}

            {showWishlist && (
                <div className="same-style header-wishlist shop-header__icon-item">
                    <LinkWrapper linkNode={staticLink("WISHLIST")} linkClass="shop-header__icon">
                        <i className="pe-7s-like" />
                        <span className="count-style">{wishlistData && wishlistData.length ? wishlistData.length : 0}</span>
                        <Label>Wishlist</Label>
                    </LinkWrapper>
                </div>
            )}

            <div className="same-style cart-wrap shop-header__icon-item shop-header__icon-item--cart" ref={cartWrapRef} onClick={closeCartOnLinkClick}>
                <button type="button" className="icon-cart shop-header__icon shop-header__cart-toggle" onClick={toggleDropdown} aria-label="Cart">
                    <i className="pe-7s-shopbag" />
                    <span className="count-style">{cartCount}</span>
                    <Label>Cart</Label>
                </button>
                <MenuCart cartData={cartData} currency={currency} removeFromCart={removeFromCart} />
                <LinkWrapper linkNode={staticLink("CART")} linkClass="shop-header__icon shop-header__cart-link">
                    <i className="pe-7s-shopbag" />
                    <span className="count-style">{cartCount}</span>
                    <Label>Cart</Label>
                </LinkWrapper>
            </div>
        </div>
    );
};

IconGroup.propTypes = {
    cartData: PropTypes.array,
    compareData: PropTypes.array,
    currency: PropTypes.object,
    removeFromCart: PropTypes.func,
    showCompare: PropTypes.bool,
    showLabels: PropTypes.bool,
    showWishlist: PropTypes.bool,
    wishlistData: PropTypes.array,
};

const mapStateToProps = (state) => {
    return {
        currency: state.currencyData,
        cartData: state.cartData,
        wishlistData: state.wishlistData,
        compareData: state.compareData,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        removeFromCart: (item) => {
            dispatch(removeFromCart(item));
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(IconGroup);